import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyGetAuditTrailEntriesQuery } from 'store/api/audit-trail';
import { buildAuditTrailFilterParams, parseAuditTrailFilterParams } from '@common/audit-trail/auditTrailUrlParams';
import { AuditActionFilter, AuditTrailFilterState, buildAuditTrailRsql } from '@common/audit-trail/buildAuditTrailRsql';
import { auditTrailSearchRsql } from './auditTrailLabels';

const PAGE_SIZE = 50;
// A single transaction is small; fetch it in one request so the grouped view is complete.
const TRANSACTION_PAGE_SIZE = 200;

export function useAuditTrailFeed() {
    // Filters live in the URL so a filtered feed — or a single transaction — is shareable and
    // survives reload and back/forward navigation.
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => parseAuditTrailFilterParams(searchParams), [searchParams]);
    const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
    const pageRef = useRef(0);
    const [fetchEntries, { data, isFetching }] = useLazyGetAuditTrailEntriesQuery();

    const applyDebouncedSearch = useDebouncedCallback((value: string) => setDebouncedSearch(value), 300);
    useEffect(() => {
        applyDebouncedSearch(filters.search);
    }, [applyDebouncedSearch, filters.search]);

    // `setSearchParams` reads the params of the last render, so two writes in one tick (the date
    // range quick-picks emit `from` and `to` separately) would overwrite each other. The pending
    // state bridges the gap until the URL has caught up.
    const pendingRef = useRef<AuditTrailFilterState | null>(null);
    useEffect(() => {
        pendingRef.current = null;
    }, [searchParams]);

    const setFilters = useCallback(
        (update: (current: AuditTrailFilterState) => AuditTrailFilterState) => {
            const next = update(pendingRef.current ?? parseAuditTrailFilterParams(searchParams));
            pendingRef.current = next;
            setSearchParams(buildAuditTrailFilterParams(next, searchParams), { replace: true });
        },
        [searchParams, setSearchParams]
    );

    // Changing any filter leaves the transaction view. Opening a transaction keeps the other
    // filters (they are simply not applied while it is open), so closing it returns to the feed
    // exactly as it was.
    const updateFilters = useCallback(
        (update: Partial<AuditTrailFilterState>) =>
            setFilters((current) => ({ ...current, ...update, transactionId: null })),
        [setFilters]
    );

    const setSearch = useCallback((search: string) => updateFilters({ search }), [updateFilters]);
    const setAction = useCallback((action: AuditActionFilter) => updateFilters({ action }), [updateFilters]);
    const setEntityTypes = useCallback((entityTypes: string[]) => updateFilters({ entityTypes }), [updateFilters]);
    const setAuthorIds = useCallback((authorIds: number[]) => updateFilters({ authorIds }), [updateFilters]);
    const setHideSystem = useCallback((hideSystem: boolean) => updateFilters({ hideSystem }), [updateFilters]);
    const setChangedAtRange = useCallback(
        (update: { from?: string | null; to?: string | null }) =>
            updateFilters({
                ...(update.from !== undefined ? { changedAtFrom: update.from } : {}),
                ...(update.to !== undefined ? { changedAtTo: update.to } : {}),
            }),
        [updateFilters]
    );

    const applyTransactionFilter = useCallback(
        (transactionId: string) => setFilters((current) => ({ ...current, transactionId })),
        [setFilters]
    );

    const clearTransactionFilter = useCallback(
        () => setFilters((current) => ({ ...current, transactionId: null })),
        [setFilters]
    );

    const rsql = useMemo(
        () => buildAuditTrailRsql({ ...filters, search: debouncedSearch }, auditTrailSearchRsql),
        [filters, debouncedSearch]
    );
    const pageSize = filters.transactionId ? TRANSACTION_PAGE_SIZE : PAGE_SIZE;

    useEffect(() => {
        pageRef.current = 0;
        fetchEntries({ filters: rsql, page: 0, pageSize });
    }, [fetchEntries, rsql, pageSize]);

    const loadMore = useCallback(() => {
        pageRef.current += 1;
        fetchEntries({ filters: rsql, page: pageRef.current, pageSize });
    }, [fetchEntries, rsql, pageSize]);

    return {
        filters,
        setSearch,
        setAction,
        setEntityTypes,
        setAuthorIds,
        setHideSystem,
        setChangedAtRange,
        applyTransactionFilter,
        clearTransactionFilter,
        entries: data?.content ?? [],
        totalElements: data?.totalElements,
        hasMore: data ? !data.last : false,
        isInitialLoading: isFetching && data === undefined,
        isFetchingMore: isFetching && data !== undefined,
        loadMore,
    };
}
